import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardMedia from "@mui/material/CardMedia";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import React, { ReactNode, useState } from "react";

export type APCardColor =
  | "neutralWhite"
  | "primary"
  | "tertiaryGreen"
  | "neutralGray"
  | "tertiaryBlue"
  | "tertiaryYellow"
  | "tertiaryDarkBlue";

export type CardColorVariant = "filled" | "outlined";

const cardColorMap: Record<
  APCardColor,
  { cardColor: string; borderColor: string | undefined }
> = {
  neutralWhite: {
    cardColor: "neutralWhite.main",
    borderColor: "outlineVariant.main",
  },
  primary: { cardColor: "primary.light", borderColor: "primary.main" },
  tertiaryGreen: {
    cardColor: "tertiaryGreen.light",
    borderColor: "tertiaryGreen.main",
  },
  neutralGray: {
    cardColor: "neutralGray.light",
    borderColor: "outlineVariant.main",
  },
  tertiaryBlue: {
    cardColor: "tertiaryBlue.light",
    borderColor: "tertiaryBlue.main",
  },
  tertiaryYellow: {
    cardColor: "tertiaryYellow.light",
    borderColor: "tertiaryYellow.main",
  },
  tertiaryDarkBlue: {
    cardColor: "tertiaryDarkBlue.light",
    borderColor: "tertiaryDarkBlue.main",
  },
};

const APCard = ({
  titleLeft,
  bodyContent,
  expandContent,
  titleRight,
  color,
  variant = "filled",
  cardImage,
  expandOnTitleClick = false,
  expandOnCardClick = false,
  onCardClick,
}: {
  titleLeft: ReactNode;
  bodyContent: ReactNode;
  expandContent?: ReactNode;
  titleRight?: ReactNode;
  color?: APCardColor;
  variant?: CardColorVariant;
  cardImage?: string;
  expandOnTitleClick?: boolean;
  expandOnCardClick?: boolean;
  onCardClick?: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const { cardColor, borderColor } = cardColorMap[color || "neutralWhite"];

  const handleExpandClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    setExpanded(!expanded);
  };

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: variant === "filled" ? cardColor : undefined,
        rowGap: 2,
        borderColor: borderColor,
      }}
      variant="outlined"
    >
      <CardActionArea
        sx={{
          p: 1.5,
          display: "flex",
          alignItems: "stretch",
          flexDirection: "column",
        }}
        onClick={expandOnCardClick ? handleExpandClick : onCardClick}
        disableRipple={true}
      >
        <Box sx={{ display: "flex" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "start",
              mr: 0.5,
            }}
          >
            {cardImage && (
              <CardMedia
                component="img"
                image={cardImage}
                sx={{ width: "1.25rem", mr: 0.5 }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box
              onClick={expandOnTitleClick ? handleExpandClick : undefined}
              sx={{ display: "flex", justifyContent: "space-between" }}
            >
              <Box>{titleLeft}</Box>
              {titleRight && <Box>{titleRight}</Box>}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 0.5,
              }}
            >
              <Box sx={{ ml: cardImage ? undefined : "1.5rem" }}>
                {bodyContent}
              </Box>
              {expandContent && (
                <IconButton
                  component="div"
                  size="small"
                  aria-label="favourite-quote"
                  color="onSurface"
                  onClick={handleExpandClick}
                >
                  {expanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
        <Collapse in={expanded}>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "1.25rem", mr: 1 }} />
            <Box sx={{ flex: 1 }}>
              <Divider
                sx={{
                  borderColor: borderColor || "outlineVariant.main",
                  my: 1,
                }}
              />
              {expandContent}
            </Box>
          </Box>
        </Collapse>
      </CardActionArea>
    </Card>
  );
};

export default APCard;
